/**
 * State, effects, and mutation handlers for the dynamic medical PA form so the
 * {@link MedicalPaForm} component stays presentational.
 *
 * @module medicalPaForm/hooks/useMedicalPaForm
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { usePharmaFormFields } from "../../../context/pharmaFormFieldsContext";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { FormDataModel } from "../../../data-model/pharmaPaFormModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { ModalId } from "../../../enums/modalId";
import { closeModal, setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { createNestedObjectFromDotKeys } from "../../../utils/createObjectFromDotNotation";
import { logError } from "../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { replaceKeyOfArrayTypeFieldWithCorrectIndex } from "../../../utils/replaceKeyOfArrayTypeFieldWithCorrectIndex";
import { replaceOptionsWithRefDocId } from "../../pharmaPaForm/utils/updateFormOptions";
import { buildFormFieldsData } from "../components/utils/buildFormFieldsData";
import {
  buildAddMoreResult,
  buildDeleteResult,
} from "../components/utils/formArrayMutations";
import { enqueueFormEvent } from "../components/utils/formEventLogger";
import { UseMedicalPaFormParams, UseMedicalPaFormReturn } from "../types";
import useSectionScrollObserver from "./useSectionScrollObserver";

/**
 * Hydrates the form from the latest saved data, resolves its configuration,
 * observes section scrolling, syncs field values into the shared form context,
 * and exposes add/delete/view handlers.
 *
 * @param params - See {@link UseMedicalPaFormParams}.
 * @returns State and handlers; see {@link UseMedicalPaFormReturn}.
 */
const useMedicalPaForm = ({
  formConfiguration,
  latestMedicalPaFormData: latestMedicalPaFormDataProp,
  scrollSectionId,
}: UseMedicalPaFormParams): UseMedicalPaFormReturn => {
  // Memoised so the empty-object fallback keeps a stable identity across
  // renders; an inline `?? {}` would be a fresh reference every commit and
  // retrigger every effect keyed on it.
  const latestMedicalPaFormData = useMemo(
    () => latestMedicalPaFormDataProp ?? {},
    [latestMedicalPaFormDataProp],
  );

  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();

  const { data: formOptions } = useSelector(
    (state: RootState) => state.formOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const dispatch = useDispatch<AppDispatch>();

  const [formConfig, setFormConfig] = useState<FormDataModel | null>(null);
  const [localMedicalPaOrder, setLocalMedicalPaOrder] =
    useState<MedicalPaOrder | null>(null);
  const [originalMedicalPaOrder, setOriginalMedicalPaOrder] =
    useState<MedicalPaOrder | null>(null);
  const { formFieldsData, setFormFieldsData, isFormDirty, setFormDirty } =
    usePharmaFormFields();
  const isDocViewerOpen = openedModalId === ModalId.DOC_VIEWER_MODAL;

  const hasLoggedToBigQuery = useRef(false);
  const orgId = getOrgIdForFetchExternalWorklist();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const clinicalDocumentFilePath = useMemo(() => {
    return generalOrderDetails?.order?.clinical_document?.file_path ?? "";
  }, [generalOrderDetails]);

  useEffect(() => {
    if (latestMedicalPaFormDataProp) {
      // The payload comes back as flat dot-notation keys
      // (e.g. "demographics.first_name"), but the form reads values via
      // `getNestedValue`, which walks a nested object. Expand the flat keys
      // into a nested structure so field lookups resolve.
      const data = createNestedObjectFromDotKeys(
        latestMedicalPaFormDataProp,
      ) as MedicalPaOrder;
      const dataWithPatientIdAndOrderId = {
        ...data,
        patient_id: generalOrderDetails?.order?.demographics?.patient_id ?? "",
        order_id: generalOrderDetails?.order?.order_id ?? "",
      };
      setOriginalMedicalPaOrder(dataWithPatientIdAndOrderId ?? null);
      setLocalMedicalPaOrder(dataWithPatientIdAndOrderId ?? null);
    }
  }, [latestMedicalPaFormDataProp]);

  useEffect(() => {
    if (formConfiguration && originalMedicalPaOrder) {
      const updatedFormConfig = replaceKeyOfArrayTypeFieldWithCorrectIndex(
        formConfiguration ?? null,
        originalMedicalPaOrder,
      );

      let cancelled = false;
      replaceOptionsWithRefDocId(updatedFormConfig ?? null, formOptions ?? [])
        .then((resolved) => {
          if (!cancelled) setFormConfig(resolved);
        })
        .catch((error) => {
          logError(
            error as Error,
            "Failed to resolve form options for medicalPaForm",
          );
        });
      return () => {
        cancelled = true;
      };
    }
  }, [formConfiguration, originalMedicalPaOrder]);

  useSectionScrollObserver(scrollSectionId, formConfig);

  useEffect(() => {
    if (originalMedicalPaOrder && formConfig) {
      setFormFieldsData(
        buildFormFieldsData(
          formConfig,
          isFormDirty ?? false,
          originalMedicalPaOrder,
          localMedicalPaOrder,
        ),
      );
      if (!hasLoggedToBigQuery.current) {
        hasLoggedToBigQuery.current = true;

        enqueueFormEvent({
          generalOrderDetails,
          orgId,
          user,
          eventName: EventsName.FORM_SCREEN_SHOWN,
          additionalData: {
            screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
            source: ScreenNamesForBigQuery.FORM_SCREEN,
            order_status:
              generalOrderDetails?.order?.status?.financial_review ?? "",
            auth_status: authOnFileDetails?.auth_on_file?.auth_status ?? "",
            payer_name: payerName,
          },
        }).catch((error) => {
          console.error("Failed to log event to BigQuery:", error);
        });
      }
    }
  }, [formConfig, generalOrderDetails, authOnFileDetails, localMedicalPaOrder]);

  // `setOpenedModalId` only records intent in Redux; the native <dialog> still
  // has to be shown imperatively, same as `orderModals`.
  useEffect(() => {
    if (isDocViewerOpen) {
      openModal(ModalId.DOC_VIEWER_MODAL);
    }
  }, [isDocViewerOpen]);

  const onAddMore = (key: string) => {
    const { updatedFormConfig, currentDataInMedicalPaOrder } =
      buildAddMoreResult(
        formFieldsData,
        latestMedicalPaFormData,
        formConfig,
        key,
      );

    enqueueFormEvent({
      generalOrderDetails,
      orgId,
      user,
      eventName: EventsName.ADD_MORE_CLICKED,
      additionalData: {
        source: key,
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        payer_name: payerName,
      },
    });
    setFormConfig(updatedFormConfig);
    setLocalMedicalPaOrder(currentDataInMedicalPaOrder);
    setFormDirty(true);
  };

  const onDelete = (path: string, parentKey: string) => {
    const { updatedFormConfig, currentDataInMedicalPaOrder } =
      buildDeleteResult(
        formFieldsData,
        latestMedicalPaFormData,
        formConfig,
        path,
        parentKey,
      );

    enqueueFormEvent({
      generalOrderDetails,
      orgId,
      user,
      eventName: EventsName.DELETE_CLICKED,
      additionalData: {
        source: parentKey,
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        payer_name: payerName,
      },
    });
    setFormConfig(updatedFormConfig);
    setLocalMedicalPaOrder(currentDataInMedicalPaOrder);
    setFormDirty(true);
  };

  const openClinicalDocument = () => {
    // Routed through the modal slice (rather than local state) so
    // `DocViewerModal` receives the oasis toolbar flag and can match
    // `filePath` against the order's clinical document to surface its
    // page-mapping navigation index.
    dispatch(
      setOpenedModalId({
        id: ModalId.DOC_VIEWER_MODAL,
        metaData: {
          filePath: clinicalDocumentFilePath,
          title: "Clinical Document",
          hideFooter: false,
          useOasisToolbar: true,
        },
      }),
    );

    enqueueFormEvent({
      generalOrderDetails,
      orgId,
      user,
      eventName: EventsName.CLINICAL_DOCUMENT_CLICKED,
      additionalData: {
        form_name: formConfig?.formTitle ?? "",
        screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
        payer_name: payerName,
      },
    });
  };

  /** Clears the modal slice so the clinical-document viewer unmounts. */
  const closeClinicalDocument = () => {
    dispatch(closeModal());
  };

  return {
    formConfig,
    formFieldsData,
    localMedicalPaOrder,
    originalMedicalPaOrder,
    clinicalDocumentFilePath,
    isDocViewerOpen,
    onAddMore,
    onDelete,
    openClinicalDocument,
    closeClinicalDocument,
  };
};

export default useMedicalPaForm;
